import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import {
  getWasteListTreatment,
  addWasteToBusiness,
  updateWaste,
  deleteWaste,
} from "../../../../../../apis/Business/WasteApi";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";
import {
  getInventoryIngoingPackagingDropdown,
  getWasteDisposalMethod,
} from "../../../../../../apis/Dropdown";

const SurplusMaterialTable = ({
  businessId,
  subcategory,
  subcategory_description,
  subcategoryId,
}) => {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [dropdownMaterial, setDropdownMaterial] = useState([]);
  const [dropdownNickname, setDropdownNickname] = useState([]);
  const [filteredNicknames, setFilteredNicknames] = useState([]);
  const [dropdownQty, setDropdownQty] = useState([]);
  const [dropdownTreatmentWasteDisposal, setDropdownTreatmentWasteDisposal] =
    useState([]);
  const [buyerOptions, setBuyerOptions] = useState([]);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  const fetchData = useCallback(() => {
    getInventoryIngoingPackagingDropdown({
      setInventoryDropdown: setDropdownMaterial,
      setInventoryNicknames: setDropdownNickname,
      setInventoryQty: setDropdownQty,
      businessId: businessId,
    });
    getWasteDisposalMethod({
      setDisposalDropdown: setDropdownTreatmentWasteDisposal,
    });
    getWasteListTreatment({
      businessId: businessId,
      setObjects: setData,
      subcategory: subcategory,
      subcategory_description: subcategory_description,
      category: "surplus materials",
      setBuyerOptions: setBuyerOptions,
      subcategoryId: subcategoryId,
    });
  }, [businessId, subcategory, subcategoryId, subcategory_description]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const dynamicPlaceholders = useMemo(
    () => ({
      "type_surplus_material.value": {
        targetField: "nickname.value",
        getPlaceholder: (selectedValue, dropdownData) => {
          const selectedNicknames = dropdownNickname.find(
            (option) => option.label === selectedValue
          );

          if (selectedNicknames && selectedNicknames.value) {
            setFilteredNicknames(
              selectedNicknames.value.map((nickname) => ({
                title: nickname,
                label: nickname,
                value: nickname,
              }))
            );
          }
        },
        dropdownDataKey: "type_surplus_material.value",
      },
      "nickname.value": {
        targetField: "amount",
        getPlaceholder: (selectedValue, dropdownData, rowData) => {
          const typeSurplusMaterial =
            rowData["type_surplus_material.value"]?.value ||
            rowData["type_surplus_material"];
          const selectedQty = dropdownQty?.find(
            (option) =>
              option?.labelMaterial === typeSurplusMaterial &&
              option?.labelNickname === selectedValue
          );

          return selectedQty ? selectedQty.values : null;
        },
        dropdownDataKey: "nickname.value",
      },
    }),
    [dropdownNickname, dropdownQty]
  );

  const handleEdit = useCallback(
    (editedItem) => {
      updateWaste({
        businessId,
        objectId: editedItem.id,
        ...editedItem,
        subcategory: subcategory,
        category: "surplus materials",
        subcategory_description: subcategory_description,
        nickname: editedItem["nickname.value"]
          ? editedItem["nickname.value"].value
          : editedItem["nickname"]
          ? editedItem["nickname"].value
          : null,
        type_surplus_material: editedItem["type_surplus_material.value"]
          ? editedItem["type_surplus_material.value"].value
          : editedItem["type_surplus_material"]
          ? editedItem["type_surplus_material"].value
          : null,
        type_current_disposal: editedItem["type_current_disposal.value"]
          ? editedItem["type_current_disposal.value"].value
          : editedItem["type_current_disposal"]
          ? editedItem["type_current_disposal"].value
          : null,
        type_surplus_energy: null,
        type_waste_water: null,
        subcategoryId: subcategoryId,
        objects: data,
        setObjects: setData,
        use_swal: true,
      });
    },
    [
      businessId,
      data,
      setData,
      subcategory,
      subcategory_description,
      subcategoryId,
    ]
  );

  const handleAdd = useCallback(
    (newItem) => {
      addWasteToBusiness({
        businessId,
        ...newItem,
        category: "surplus materials",
        nickname: newItem["nickname.value"]
          ? newItem["nickname.value"].value
          : null,
        type_surplus_material: newItem["type_surplus_material.value"]
          ? newItem["type_surplus_material.value"].value
          : null,
        type_current_disposal: newItem["type_current_disposal.value"]
          ? newItem["type_current_disposal.value"].value
          : null,
        subcategory: subcategory,
        subcategory_description: subcategory_description,
        subcategoryId: subcategoryId,
        type_surplus_energy: null,
        type_waste_water: null,
        callback: fetchData,
        use_swal: true,
      });
    },
    [businessId, fetchData, subcategory, subcategory_description, subcategoryId]
  );

  const handleDelete = useCallback(
    (itemToDelete) => {
      deleteWaste({
        businessId,
        objectId: itemToDelete.id,
        callback: fetchData,
      });
    },
    [businessId, fetchData]
  );

  const columns = useMemo(
    () => [
      {
        accessor: "invoice_date",
        header: t("INVOICE_DATE"),
        type: "text",
        canHide: true,
        canSort: true,
        placeholder: t("DD_MM_YYYY"),
        optional: true,
        width: "80px",
      },
      {
        accessor: "invoice_number",
        header: t("INVOICE_NO"),
        type: "text",
        canSort: true,
        canHide: true,
        optional: true,
        width: "80px",
      },
      {
        accessor: "nickname.value",
        header: t("NICKNAME"),
        type: "dropdown",
        dropdownData: filteredNicknames,
        disableSortBy: true,
        width: "110px",
        optional: true,
        tipHeader: "Tip",
        tipBody: t("TIPBOX_WASTE_SURPLUS"),
      },
      {
        accessor: "type_surplus_material.value",
        header: `${t("WASTE_ONLY")}*`,
        type: "dropdown",
        dropdownData: dropdownMaterial,
        disableSortBy: true,
        width: "110px",
        optional: true,
        tipHeader: "Tip",
        tipBody: t("TIPBOX_WASTE_SURPLUS"),
      },
      {
        accessor: "type_current_disposal.value",
        header: `${t("CURRENT_DISPOSAL")}*`,
        type: "dropdown",
        dropdownData: dropdownTreatmentWasteDisposal,
        disableSortBy: true,
        width: "110px",
        optional: true,
      },
      {
        accessor: "amount",
        header: `${t("QUANTITY")}*`,
        type: "number",
        disableSortBy: true,
        placeholder: t("WASTE_QTY"),
        width: "80px",
        optional: true,
      },
      {
        accessor: "is_reused",
        header: t("IS_REUSED"),
        type: "switch",
        disableSortBy: true,
        width: "100px",
        optional: true,
      },

      {
        accessor: "spacer1",
        header: "",
        type: "spacer",
        width: "20px",
        optional: true,
        disableSortBy: true,
      },
      {
        accessor: "price_invoice",
        header: t("PRICE_INVOICE"),
        type: "number",
        canSort: true,
        canHide: true,
        placeholder: "0",
        optional: true,
        subtotalUnit: "DKK",
        width: "80px",
        tipHeader: "Tip",
        tipBody: t("PRICE_INVOICE_TIP"),
      },
      {
        accessor: "carbon",
        header: t("CARBON_OUTPUT"),
        type: "custom",
        canSort: true,
        cell: ({ value }) => {
          return <CustomInputComponent value={value} readOnly />;
        },
        optional: true,
        subtotalUnit: "tCO₂e",
        subtotalFixed: true,
        width: "80px",
      },
      {
        accessor: "spacer2",
        header: "",
        type: "spacer",
        width: "20px",
        optional: true,
        disableSortBy: true,
      },
      {
        accessor: "share",
        header: t("SHARE"),
        type: "checkbox",
        wide: false,
        width: "50px",
        tipHeader: "Tip",
        tipBody: t("TIPBOX_WASTE_SELL"),
        optional: true,
        disableSortBy: true,
      },
      {
        accessor: "buyer",
        header: t("BUYER"),
        type: "buyerInput",
        buyerOptions: buyerOptions,
        width: "100px",
        optional: true,
      },
      {
        accessor: "surplus_materials_sold",
        header: t("QTY_SOLD_MATERIALS"),
        type: "number",
        disableSortBy: true,
        placeholder: "0",
        width: "80px",
        optional: true,
      },
      {
        accessor: "avoided_emission",
        header: t("AVOIDED_EMISSIONS"),
        type: "custom",
        canSort: true,
        cell: ({ value }) => {
          return <CustomInputComponent value={value} readOnly />;
        },
        optional: true,
        subtotalUnit: "tCO₂e",
        subtotalFixed: true,
        width: "80px",
      },
    ],
    [
      dropdownMaterial,
      dropdownTreatmentWasteDisposal,
      buyerOptions,
      filteredNicknames,
      t,
    ]
  );

  const disableRules = {
    carbon: () => true,
    avoided_emission: () => true,
    surplus_materials_sold: () => true,
    buyer: () => true,
    share: () => true,
  };

  const subtotalColumns = {
    price_invoice: { label: t("TOTAL_PRICE") },
    carbon: { label: t("TOTAL_CARBON") },
    avoided_emission: { label: t("AVOIDED_EMISSIONS") },
  };

  const memoizedDropdownData = useMemo(
    () => ({
      "nickname.value": filteredNicknames,
      "type_surplus_material.value": dropdownMaterial,
      "type_current_disposal.value": dropdownTreatmentWasteDisposal,
    }),
    [filteredNicknames, dropdownMaterial, dropdownTreatmentWasteDisposal]
  );

  return (
    <CustomTable
      columns={columns}
      data={data}
      setData={setData}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onDelete={handleDelete}
      dropdownData={memoizedDropdownData}
      subtotalColumns={subtotalColumns}
      customInputs={{
        carbon: CustomInputComponent,
        avoided_emission: CustomInputComponent,
      }}
      hiddenColumns={hiddenColumns}
      toggleColumnVisibility={toggleColumnVisibility}
      disableRules={disableRules}
      dynamicPlaceholders={dynamicPlaceholders}
    />
  );
};

export default SurplusMaterialTable;
